import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button, Typography, Grid } from "@mui/material";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { fetchCoursePackages } from "../../../redux/actions/contentCoursesActions";
import { makeGetEntities } from "../../../redux/selectors/selectors";
import Modal from "../../../components/Modal";
import Select from "../../../components/FormikFields/Select";
import {
  courseTypesForAdmin,
  trainingMinutesOptions
} from "../../../constants";
import useScopedTranslation from "../../../hooks/useScopedTranslation";
import { useTranslation } from "react-i18next";
import { useSnackbarNotifications } from "../../../hooks/snackbar";
import { some } from "lodash";

const selector = makeGetEntities();

const minuteMenuItems = trainingMinutesOptions.map(option => ({
  value: option,
  label: option
}));

const descriptionMaxCharacters = 800;

const getNewCourseSchema = t =>
  Yup.object().shape({
    name: Yup.string().required(t("validationMessages.nameRequired")),
    durationHours: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber"))
      .required(t("validationMessages.fieldRequired")),
    coursePackage: Yup.string().required(
      t("validationMessages.manualRequired")
    ),
    number: Yup.string().required(t("validationMessages.policyNumberRequired")),
    description: Yup.string().max(
      800,
      t("validationMessages.cannotExceedLength", {
        type: t(
          "managePolicies.addPolicyModal.description.charactersRemaining"
        ),
        maxLength: 800
      })
    )
  });

const parseResponseErrors = errors => {
  if (
    some(
      errors,
      error => error.detail === "Number must be unique within manual"
    )
  ) {
    return { number: "managePolicies.error.duplicateNumber" };
  }
  return false;
};

const AddNewPolicyForm = ({ packages, values, open, resetForm }) => {
  const t = useScopedTranslation("managePolicies.addPolicyModal");

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Form>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Field
            variant="filled"
            fullWidth
            label={t("name.label")}
            component={TextField}
            name="name"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={5}>
          <Field
            variant="filled"
            fullWidth
            label={t("number.label")}
            component={TextField}
            name="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field
            variant="filled"
            fullWidth
            label={t("hours.label")}
            component={TextField}
            name="durationHours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Select
            name="durationMinutes"
            menuItems={minuteMenuItems}
            label={t("minutes.label")}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <Select
            name="coursePackage"
            menuItems={
              packages
                ? packages.map(coursePackage => ({
                    value: coursePackage.id,
                    label: coursePackage.name
                  }))
                : []
            }
            label={t(
              `package.${values.coursePackage ? "label" : "blankLabel"}`
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Field
            variant="filled"
            fullWidth
            label={t("description.label")}
            component={TextField}
            name="description"
            multiline
            helperText={`${descriptionMaxCharacters -
              values.description.length}/${descriptionMaxCharacters} ${t(
              "description.charactersRemaining"
            )}`}
            rows={4}
          />
        </Grid>
      </Grid>
    </Form>
  );
};

AddNewPolicyForm.propTypes = {
  packages: PropTypes.any,
  values: PropTypes.any
};

function AddNewPolicyModal({ createPolicy, onResult }) {
  const t = useScopedTranslation("managePolicies.addPolicyModal");
  const { t: globalT } = useTranslation();

  const { displayErrorMessage } = useSnackbarNotifications();

  const { packages } = useSelector(({ contentCoursesState }) => ({
    packages: selector(contentCoursesState, {
      type: "coursePackage"
    })
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCoursePackages("manual"));
  }, []);

  const onSubmitPolicy = async (values, { setFieldError }) => {
    const vars = { ...values };
    delete vars.contentType;

    try {
      const response = await createPolicy(vars);
      if (response.errors) {
        const parsedErrors = parseResponseErrors(response.errors);
        if (parsedErrors.number) {
          setFieldError("number", globalT(parsedErrors.number));
        } else {
          throw response.errors;
        }
      } else {
        onResult(response);
      }
    } catch (e) {
      console.error(e);
      displayErrorMessage(globalT("managePolicies.error.create"));
    }
  };

  return (
    <Formik
      initialValues={{
        name: "",
        number: "",
        durationHours: 0,
        durationMinutes: 0,
        courseType: courseTypesForAdmin.online,
        coursePackage: "",
        description: ""
      }}
      validationSchema={getNewCourseSchema(globalT)}
      enableReinitialize
      onSubmit={onSubmitPolicy}
    >
      {({ submitForm, isSubmitting, values, resetForm }) => (
        <Modal
          scrollable
          maxWidth="md"
          fullWidth
          open={true}
          dialogTitle={
            <Typography variant="h4" color="textPrimary">
              {t("title")}
            </Typography>
          }
          dialogContent={
            <AddNewPolicyForm
              packages={packages}
              values={values}
              resetForm={resetForm}
            />
          }
          dialogActions={
            <>
              <Button
                color="primary"
                disabled={isSubmitting}
                onClick={() => onResult(false)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={submitForm}
                disabled={isSubmitting}
              >
                {t("submit")}
              </Button>
            </>
          }
          onClose={() => onResult(false)}
          showCloseIcon
        />
      )}
    </Formik>
  );
}

AddNewPolicyModal.propTypes = {
  createPolicy: PropTypes.func,
  onResult: PropTypes.func.isRequired
};

export default AddNewPolicyModal;
